import React, { useContext, useEffect, useRef } from "react";
import "./loop.scss";
import { FormattedText, getTextWithTooltip } from "../../../utils/helpers";
import { DashboardContext } from "../../../context/DashboardContext";
import { getModelById } from "../../leftSide/Models/modelLogic";
import deleteIcon from "../../../res/icons/delete.png";

function TrackedParamDisplay(props) {
  const { listOfTrackables, onRemoveParameter } = props;
  const { modelData } = useContext(DashboardContext);
  const hasCleanedUp = useRef(false);

  // Helper function to find parameter and get its name
  const findParameter = (model, parameterToTrack) => {
    if (!model) return null;
    
    if (parameterToTrack.lineshape_id !== null) {
      // Lineshape parameter
      const lineshape = model.lineshapeData?.find(ls => ls.lineshape_id === parameterToTrack.lineshape_id);
      if (!lineshape || !lineshape.lineParams?.[parameterToTrack.rowIndex]) {
        return null;
      }
      return lineshape.lineParams[parameterToTrack.rowIndex].find(
        p => p.speqqle_id === parameterToTrack.speqqle_id
      );
    } else if (parameterToTrack.rowIndex !== null) {
      // Recurring parameter
      const recParam = model.recParams?.[parameterToTrack.rowIndex];
      if (!recParam) {
        return null;
      }
      return recParam.find(
        p => p.speqqle_id === parameterToTrack.speqqle_id
      );
    } else {
      // Non-recurring parameter
      return model.modelParams?.find(
        p => p.speqqle_id === parameterToTrack.speqqle_id
      );
    }
  };

  // Use useEffect to clean up invalid parameters after render
  useEffect(() => {
    if (hasCleanedUp.current) return;
    hasCleanedUp.current = true;

    // Clean up model sets where the model no longer exists
    listOfTrackables.forEach((modelSet) => {
      const model = getModelById(modelSet.FE_ID, modelData);
      if (!model) {
        // Model not found, remove all parameters for this model
        modelSet.selectedParams.forEach((param) => {
          onRemoveParameter(modelSet.FE_ID, param);
        });
        return;
      }

      // Clean up parameters that no longer exist in the model
      modelSet.selectedParams.forEach((param) => {
        const foundParam = findParameter(model, param);
        if (!foundParam) {
          onRemoveParameter(modelSet.FE_ID, param);
        }
      });
    });

    // Reset the flag after a short delay to allow for future cleanups
    setTimeout(() => {
      hasCleanedUp.current = false;
    }, 100);
  }, [listOfTrackables, modelData, onRemoveParameter]);

  // Filter out model sets where the model no longer exists (for display only)
  const validModelSets = listOfTrackables.filter((modelSet) => {
    const model = getModelById(modelSet.FE_ID, modelData);
    return model !== null;
  });

  return (
    <div className="allTrackedParams">
      {validModelSets.map((modelSet) => {
        const model = getModelById(modelSet.FE_ID, modelData);
        const modelName = model ? model.displayName : "Unknown Model";
        
        return (
          <div className="modelSet" key={modelSet.FE_ID}>
            <div className="title">{getTextWithTooltip(modelName, 25)}&nbsp;:</div>
            <div className="parameters">
              {modelSet.selectedParams.map((parameterToTrack, index) => {
                // Find the actual parameter in the model to get its name
                const param = findParameter(model, parameterToTrack);
                const paramName = param ? param.name : "Unknown Parameter";
                
                return (
                  <div className="trackedParameter" key={index}>
                    <div className="content">
                      <div className="paramName">
                        <FormattedText input={paramName} />
                      </div>
                      {parameterToTrack.rowIndex !== null ? (
                        <div className="row">
                          Row {parameterToTrack.rowIndex + 1}
                        </div>
                      ) : null}
                    </div>
                    <img
                      src={deleteIcon}
                      alt="Remove parameter from tracking"
                      className="deleteButton"
                      onClick={() => onRemoveParameter(modelSet.FE_ID, parameterToTrack)}
                      title="Remove parameter from tracking"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default TrackedParamDisplay;
